import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getCustomerOutstandingDocuments, createCustomerPayment } from '../services/customerService';
import { fetchPaymentMethods } from '../../../app/store/slices/paymentMethodSlice';

const CustomerPaymentDialog = ({ open, onClose, customer, onPaymentSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const paymentMethodsFromRedux = useSelector(state => state.paymentMethod.paymentMethods);
  const paymentMethodsLoading = useSelector(state => state.paymentMethod.loading);
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [error, setError] = useState(null);
  
  // Payment form state
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMethodId, setPaymentMethodId] = useState(''); // Empty by default
  const [notes, setNotes] = useState('');
  const [notifyViaEmail, setNotifyViaEmail] = useState(false);
  const [notifyViaWhatsApp, setNotifyViaWhatsApp] = useState(false);
  
  // Selected documents
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  
  // Partial payment tracking - stores the amount being paid for each document
  const [documentPayments, setDocumentPayments] = useState({});

  // Fetch payment methods on mount
  useEffect(() => {
    if (paymentMethodsFromRedux.length === 0 && !paymentMethodsLoading) {
      dispatch(fetchPaymentMethods());
    }
  }, [dispatch, paymentMethodsFromRedux.length, paymentMethodsLoading]);

  // Reset dialog state when it opens
  const resetDialogState = () => {
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMethodId(''); // Empty by default
    setNotes('');
    setNotifyViaEmail(false);
    setNotifyViaWhatsApp(false);
    setSelectedDocuments([]);
    setDocuments([]);
    setError(null);
  };

  useEffect(() => {
    if (open && customer) {
      resetDialogState();
      fetchOutstandingDocuments();
    }
  }, [open, customer]);

  // Auto-populate notes with selected document numbers
  useEffect(() => {
    if (selectedDocuments.length > 0) {
      const selectedDocNumbers = selectedDocuments
        .map(sel => {
          const doc = documents.find(d => d.DocumentId === sel.DocumentId && d.DocumentType === sel.DocumentType);
          return doc?.DocumentNumber;
        })
        .filter(Boolean)
        .join(', ');
      
      if (selectedDocNumbers) {
        setNotes(`This payment is for: ${selectedDocNumbers}`);
      }
    } else {
      // Clear notes when no documents selected
      setNotes('');
    }
  }, [selectedDocuments, documents]);

  const fetchOutstandingDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCustomerOutstandingDocuments(customer.Id);
      
      let documentsData = [];
      if (response?.payload && Array.isArray(response.payload)) {
        documentsData = response.payload;
      } else if (Array.isArray(response)) {
        documentsData = response;
      }
      
      // Sort documents by date - latest first (for display)
      const sortedData = [...documentsData].sort((a, b) => {
        const dateA = new Date(a.DocumentDate || 0);
        const dateB = new Date(b.DocumentDate || 0);
        return dateB - dateA; // Latest first
      });
      
      setDocuments(sortedData);
      
    } catch (err) {
      setError(err.message || 'Failed to load outstanding documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentToggle = (document) => {
    setSelectedDocuments(prev => {
      const exists = prev.find(d => d.DocumentId === document.DocumentId && d.DocumentType === document.DocumentType);
      if (exists) {
        // Remove from selection
        const updated = prev.filter(d => !(d.DocumentId === document.DocumentId && d.DocumentType === document.DocumentType));
        
        // Update payment amount to selected total
        if (updated.length > 0) {
          const newTotal = documents
            .filter(doc => updated.find(sel => sel.DocumentId === doc.DocumentId && sel.DocumentType === doc.DocumentType))
            .reduce((sum, doc) => sum + (doc.DueAmount || 0), 0);
          setPaymentAmount(newTotal.toString());
          
          // Update document payments for manual selection
          const newPayments = {};
          documents.forEach(doc => {
            const isInSelection = updated.find(sel => sel.DocumentId === doc.DocumentId && sel.DocumentType === doc.DocumentType);
            if (isInSelection) {
              const docKey = `${doc.DocumentType}-${doc.DocumentId}`;
              newPayments[docKey] = doc.DueAmount || 0; // Full payment for manually selected docs
            }
          });
          setDocumentPayments(newPayments);
        } else {
          setPaymentAmount('');
          setDocumentPayments({});
        }
        
        return updated;
      } else {
        // Add to selection
        const updated = [...prev, { DocumentId: document.DocumentId, DocumentType: document.DocumentType }];
        
        // Update payment amount to selected total
        const newTotal = documents
          .filter(doc => updated.find(sel => sel.DocumentId === doc.DocumentId && sel.DocumentType === doc.DocumentType))
          .reduce((sum, doc) => sum + (doc.DueAmount || 0), 0);
        setPaymentAmount(newTotal.toString());
        
        // Update document payments for manual selection
        const newPayments = {};
        documents.forEach(doc => {
          const isInSelection = updated.find(sel => sel.DocumentId === doc.DocumentId && sel.DocumentType === doc.DocumentType);
          if (isInSelection) {
            const docKey = `${doc.DocumentType}-${doc.DocumentId}`;
            newPayments[docKey] = doc.DueAmount || 0; // Full payment for manually selected docs
          }
        });
        setDocumentPayments(newPayments);
        
        return updated;
      }
    });
  };

  const handleFullPayment = () => {
    setPaymentAmount(totalDueAmount.toString());
    // Auto-allocate for full payment
    autoAllocatePayment(totalDueAmount);
  };

  // Auto-allocate payment amount from oldest to newest documents
  const autoAllocatePayment = (amount) => {
    const paymentAmt = parseFloat(amount);
    if (!paymentAmt || paymentAmt <= 0 || documents.length === 0) {
      setSelectedDocuments([]);
      setDocumentPayments({});
      return;
    }

    // Sort documents oldest first for allocation
    const sortedDocs = [...documents].sort((a, b) => {
      const dateA = new Date(a.DocumentDate || 0);
      const dateB = new Date(b.DocumentDate || 0);
      return dateA - dateB; // Oldest first
    });

    let remainingAmount = paymentAmt;
    const selected = [];
    const payments = {};

    for (const doc of sortedDocs) {
      if (remainingAmount <= 0) break;

      const dueAmount = doc.DueAmount || 0;
      const docKey = `${doc.DocumentType}-${doc.DocumentId}`;

      if (remainingAmount >= dueAmount) {
        // Full payment for this document
        selected.push({ DocumentId: doc.DocumentId, DocumentType: doc.DocumentType });
        payments[docKey] = dueAmount;
        remainingAmount -= dueAmount;
      } else {
        // Partial payment for this document
        selected.push({ DocumentId: doc.DocumentId, DocumentType: doc.DocumentType });
        payments[docKey] = remainingAmount;
        remainingAmount = 0;
      }
    }

    setSelectedDocuments(selected);
    setDocumentPayments(payments);
  };

  // Handle manual payment amount change
  const handlePaymentAmountChange = (e) => {
    const value = e.target.value;
    setPaymentAmount(value);
    
    // Auto-allocate when user manually enters amount
    if (value && parseFloat(value) > 0) {
      autoAllocatePayment(parseFloat(value));
    } else {
      setSelectedDocuments([]);
      setDocumentPayments({});
    }
  };

  const handleSubmit = async () => {
    // Validation
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error(t('please_enter_valid_amount') || 'Please enter a valid payment amount');
      return;
    }
    
    if (!paymentMethodId) {
      toast.error(t('please_select_payment_method') || 'Please select a payment method');
      return;
    }

    try {
      setSubmitting(true);
      
      const paymentData = {
        CustomerId: customer.Id,
        PaymentAmount: parseFloat(paymentAmount),
        PaymentDate: new Date(paymentDate).toISOString(),
        PaymentMethodId: paymentMethodId,
        Notes: notes || '',
        TargetDocuments: selectedDocuments.length > 0 ? selectedDocuments : [],
        NotifyViaWhatsApp: notifyViaWhatsApp,
        NotifyViaEmail: notifyViaEmail,
      };
      
      await createCustomerPayment(paymentData);
      
      toast.success(t('payment_created_successfully') || 'Payment created successfully!');
      
      // Call success callback
      if (onPaymentSuccess) {
        onPaymentSuccess();
      }
      
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || t('payment_creation_failed') || 'Failed to create payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '-';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const handleCopyDocumentNumber = (documentNumber, event) => {
    event.stopPropagation(); // Prevent row selection when clicking copy
    navigator.clipboard.writeText(documentNumber);
    toast.success(t('copied_to_clipboard') || `Copied: ${documentNumber}`);
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      'Paid': 'success',
      'PAID': 'success',
      'PARTIALLY_PAID': 'warning',
      'Partially Paid': 'warning',
      'Not Paid': 'error',
      'NOT_PAID': 'error',
      'Unpaid': 'error',
    };
    return colors[status] || 'default';
  };

  const totalDueAmount = documents.reduce((sum, doc) => sum + (doc.DueAmount || 0), 0);
  const selectedTotal = documents
    .filter(doc => selectedDocuments.find(sel => sel.DocumentId === doc.DocumentId && sel.DocumentType === doc.DocumentType))
    .reduce((sum, doc) => sum + (doc.DueAmount || 0), 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, m: { xs: 1, sm: 3 }, width: { xs: 'calc(100% - 16px)', sm: 'auto' } }
      }}
    >
      <DialogTitle sx={{ pb: 2, px: { xs: 1.5, sm: 3 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaymentIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {t('make_payment') || 'Make Payment'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent sx={{ pt: { xs: 2, sm: 3 }, px: { xs: 1.5, sm: 3 }, overflowX: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 200, gap: 2 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              {t('loading_outstanding_documents') || 'Loading outstanding documents...'}
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box>
            {/* Customer Info & Payment Summary */}
            <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
              <Grid container spacing={2}>
                {/* Customer Details */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('customer') || 'Customer'}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {customer?.CustomerName || customer?.Name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {customer?.Email || customer?.email}
                  </Typography>
                </Grid>
                
                {/* Payment Summary */}
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        {t('total_outstanding') || 'Total Outstanding'}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'error.main' }}>
                        {formatCurrency(totalDueAmount)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        {t('payment_amount') || 'Payment Amount'}
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                        {formatCurrency(parseFloat(paymentAmount) || 0)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>

            {/* Outstanding Documents */}
            {documents.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('outstanding_documents') || 'Outstanding Documents'}
                </Typography>
                <TableContainer component={Paper} sx={{ boxShadow: 1, overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#fafafa' }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={selectedDocuments.length > 0 && selectedDocuments.length < documents.length}
                            checked={selectedDocuments.length === documents.length && documents.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all documents and auto-allocate full payment
                                const total = documents.reduce((sum, doc) => sum + (doc.DueAmount || 0), 0);
                                setPaymentAmount(total.toString());
                                autoAllocatePayment(total);
                              } else {
                                // Deselect all
                                setSelectedDocuments([]);
                                setDocumentPayments({});
                                setPaymentAmount('');
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('document_type') || 'Type'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('document_number') || 'Number'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('date') || 'Date'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">{t('total_amount') || 'Total'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">{t('paid_amount') || 'Paid'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">{t('due_amount') || 'Due'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="right">{t('paying') || 'Paying'}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{t('status') || 'Status'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {documents.map((doc) => {
                        const isSelected = selectedDocuments.find(
                          s => s.DocumentId === doc.DocumentId && s.DocumentType === doc.DocumentType
                        );
                        const docKey = `${doc.DocumentType}-${doc.DocumentId}`;
                        const payingAmount = documentPayments[docKey] || 0;
                        const dueAmount = doc.DueAmount || 0;
                        const isPartialPayment = isSelected && payingAmount > 0 && payingAmount < dueAmount;
                        
                        return (
                          <TableRow
                            key={`${doc.DocumentType}-${doc.DocumentId}`}
                            hover
                            onClick={() => handleDocumentToggle(doc)}
                            sx={{ 
                              cursor: 'pointer', 
                              bgcolor: isSelected ? (isPartialPayment ? '#fff3e0' : '#e3f2fd') : 'inherit'
                            }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={!!isSelected} />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.DocumentType}
                                size="small"
                                color={doc.DocumentType === 'Invoice' ? 'primary' : 'secondary'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {doc.DocumentNumber}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleCopyDocumentNumber(doc.DocumentNumber, e)}
                                  sx={{ 
                                    padding: '2px',
                                    '&:hover': { bgcolor: 'action.hover' }
                                  }}
                                >
                                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                            <TableCell>{formatDate(doc.DocumentDate)}</TableCell>
                            <TableCell align="right">{formatCurrency(doc.TotalAmount)}</TableCell>
                            <TableCell align="right" sx={{ color: 'success.main' }}>
                              {formatCurrency(doc.TotalPaidAmount)}
                            </TableCell>
                            <TableCell align="right" sx={{ color: 'error.main', fontWeight: 700 }}>
                              {formatCurrency(dueAmount)}
                            </TableCell>
                            <TableCell align="right">
                              {isSelected && payingAmount > 0 ? (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main' }}>
                                    {formatCurrency(payingAmount)}
                                  </Typography>
                                  {isPartialPayment && (
                                    <Typography variant="caption" sx={{ color: 'warning.main', display: 'block' }}>
                                      {formatCurrency(dueAmount - payingAmount)} {t('remaining') || 'left'}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={doc.PaymentStatus?.replace('_', ' ')}
                                size="small"
                                color={getPaymentStatusColor(doc.PaymentStatus)}
                                sx={{ fontWeight: 600, fontSize: '0.7rem' }}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                {selectedDocuments.length > 0 && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      {selectedDocuments.length} {t('documents_selected') || 'document(s) selected'} - 
                      <strong> {t('selected_total') || 'Selected Total'}: {formatCurrency(selectedTotal)}</strong>
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {documents.length === 0 && (
              <Alert severity="info" sx={{ mb: 3 }}>
                {t('no_outstanding_documents') || 'No outstanding documents found. You can still make a general payment.'}
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            {/* Payment Details */}
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              {t('payment_details') || 'Payment Details'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    fullWidth
                    label={t('payment_amount') || 'Payment Amount'}
                    type="number"
                    value={paymentAmount}
                    onChange={handlePaymentAmountChange}
                    required
                    InputProps={{
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleFullPayment}
                    disabled={totalDueAmount === 0}
                    sx={{ 
                      minWidth: { xs: 0, sm: 'auto' },
                      width: { xs: '100%', sm: 'auto' },
                      px: 2,
                      height: '56px',
                      whiteSpace: 'nowrap',
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    {t('full_payment') || 'Full Payment'}
                  </Button>
                </Box>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={t('payment_date') || 'Payment Date'}
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  required
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0]
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth required>
                  <InputLabel id="payment-method-label">{t('payment_method') || 'Payment Method'}</InputLabel>
                  <Select
                    labelId="payment-method-label"
                    value={paymentMethodId}
                    onChange={(e) => setPaymentMethodId(e.target.value)}
                    label={t('payment_method') || 'Payment Method'}
                  >
                    <MenuItem value="" disabled>
                      <em>{t('select_payment_method') || 'Select payment method'}</em>
                    </MenuItem>
                    {paymentMethodsFromRedux.map((method) => (
                      <MenuItem key={method.id} value={method.id}>
                        {method.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('notes') || 'Notes'}
                  multiline
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('add_payment_notes') || 'Add any notes about this payment...'}
                  inputProps={{ maxLength: 1000 }}
                  helperText={`${notes.length}/1000 ${t('characters') || 'characters'}`}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t('notifications') || 'Notifications'}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifyViaEmail}
                      onChange={(e) => setNotifyViaEmail(e.target.checked)}
                      disabled
                    />
                  }
                  label={t('send_receipt_via_email') || 'Send payment receipt via email (Coming soon)'}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={notifyViaWhatsApp}
                      onChange={(e) => setNotifyViaWhatsApp(e.target.checked)}
                      disabled
                    />
                  }
                  label={t('send_receipt_via_whatsapp') || 'Send payment receipt via WhatsApp (Coming soon)'}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
      
      <Divider />
      
      <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' }, '& > button': { width: { xs: '100%', sm: 'auto' } } }}>
        <Button onClick={onClose} disabled={submitting}>
          {t('cancel') || 'Cancel'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={loading || submitting || !paymentAmount || parseFloat(paymentAmount) <= 0 || !paymentMethodId}
          startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
        >
          {submitting ? (t('processing') || 'Processing...') : (t('pay') || 'Pay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomerPaymentDialog;
